import { useEffect, useState } from 'react';
import { Check, ArrowRight, ShieldCheck, Info } from 'lucide-react';
import { Avatar, Button, Chip, Field, Input, KV, Modal } from '../components/ui';
import { useSections, useCreateMember } from '../api/queries';
import { useToast } from '../lib/toast';
import { initials, MITGLIEDSART_LABEL, MITGLIEDSART_INFO, isUnder18 } from '../lib/format';
import { isValidEmail, isValidPhone, isValidPlz } from '../lib/validate';

const STEPS = ['Persönliches', 'Kontakt', 'Sektionen', 'Übersicht'];

const FUNKTIONEN = [
  'Mitglied', 'Obmann', 'Obmann Stv.', 'Sektionsleiter', 'Sektionsleiter Stv.',
  'Kassier', 'Kassier Stv.', 'Schriftführer', 'Schriftführer Stv.',
  'Beirat', 'Trainer', 'Spieler',
];

interface Form {
  vorname: string;
  nachname: string;
  geburtsdatum: string;
  geschlecht: 'm' | 'w' | 'd';
  strasse: string;
  plz: string;
  ort: string;
  telefon: string;
  email: string;
  mitgliedsart: 'aktiv' | 'unterstuetzend' | 'jugend' | 'ehrenmitglied';
  eingetreten_am: string;
}

const EMPTY: Form = {
  vorname: '', nachname: '', geburtsdatum: '', geschlecht: 'm',
  strasse: '', plz: '4673', ort: 'Gaspoltshofen', telefon: '', email: '',
  mitgliedsart: 'aktiv',
  eingetreten_am: '',
};

export function CreateMemberWizard({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { data: sections = [] } = useSections();
  const create = useCreateMember();
  const toast = useToast();
  const [step, setStep] = useState(0);
  const [secs, setSecs] = useState<Map<string, string>>(new Map());
  const [form, setForm] = useState<Form>(EMPTY);

  useEffect(() => {
    if (open) {
      setStep(0);
      setSecs(new Map());
      setForm(EMPTY);
    }
  }, [open]);

  const upd = <K extends keyof Form>(k: K, v: Form[K]) => setForm((f) => ({ ...f, [k]: v }));
  const toggleSec = (id: string) => {
    setSecs((prev) => {
      const next = new Map(prev);
      if (next.has(id)) next.delete(id);
      else next.set(id, 'Mitglied');
      return next;
    });
  };
  const setSecFn = (id: string, fn: string) => {
    setSecs((prev) => {
      const next = new Map(prev);
      next.set(id, fn);
      return next;
    });
  };

  const emailErr = form.email && !isValidEmail(form.email) ? 'Bitte eine gültige E-Mail eingeben.' : null;
  const phoneErr = form.telefon && !isValidPhone(form.telefon) ? 'Telefonnummer wirkt ungültig.' : null;
  const plzErr = form.plz && !isValidPlz(form.plz) ? 'PLZ muss vierstellig sein.' : null;

  const forceJugend = isUnder18(form.geburtsdatum);
  const effectiveMitgliedsart = forceJugend ? 'jugend' : form.mitgliedsart;

  const canStep0 = !!form.vorname.trim() && !!form.nachname.trim();
  const canStep1 = !emailErr && !phoneErr && !plzErr;
  const canSubmit = canStep0 && canStep1;

  const submit = () => {
    if (!canSubmit) return;
    create.mutate(
      {
        vorname: form.vorname.trim(),
        nachname: form.nachname.trim(),
        geburtsdatum: form.geburtsdatum || null,
        geschlecht: form.geschlecht,
        strasse: form.strasse,
        plz: form.plz,
        ort: form.ort,
        telefon: form.telefon,
        email: form.email,
        mitgliedsart: effectiveMitgliedsart,
        eingetreten_am: form.eingetreten_am || null,
        sektionen: [...secs.keys()],
        funktionen: Object.fromEntries(secs),
      },
      {
        onSuccess: (r) => {
          toast.push(`Mitglied #${r.mitgliedsnummer} angelegt`);
          onClose();
        },
        onError: () => toast.push('Mitglied konnte nicht angelegt werden', 'warn'),
      },
    );
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Neues Mitglied anlegen"
      size="lg"
      footer={
        <>
          {step > 0 ? (
            <Button variant="ghost" onClick={() => setStep((s) => s - 1)}>Zurück</Button>
          ) : (
            <Button variant="ghost" onClick={onClose}>Abbrechen</Button>
          )}
          <div style={{ flex: 1 }} />
          {step < 3 ? (
            <Button
              variant="primary"
              iconRight={<ArrowRight size={16} />}
              onClick={() => setStep((s) => s + 1)}
              disabled={
                (step === 0 && !canStep0) ||
                (step === 1 && !canStep1)
              }
            >
              Weiter
            </Button>
          ) : (
            <Button variant="primary" icon={<Check size={16} />} onClick={submit} disabled={create.isPending || !canSubmit}>
              Mitglied speichern
            </Button>
          )}
        </>
      }
    >
      {/* Progress */}
      <div style={{ marginBottom: 20 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
          {STEPS.map((s, i) => (
            <div
              key={i}
              style={{
                fontSize: 12,
                fontWeight: i === step ? 600 : 500,
                color: i === step ? 'var(--accent)' : i < step ? 'var(--text-secondary)' : 'var(--text-quaternary)',
                display: 'flex', alignItems: 'center', gap: 5,
              }}
            >
              {i < step && <Check size={11} />}
              {s}
            </div>
          ))}
        </div>
        <div className="progress-track">
          <div className="progress-fill" style={{ width: `${(step / 3) * 100}%` }} />
        </div>
      </div>

      <div style={{ minHeight: 240 }}>
        {step === 0 && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            <div className="grid-2">
              <Field label="Vorname" required><Input value={form.vorname} onChange={(e) => upd('vorname', e.target.value)} placeholder="z. B. Anna" autoFocus /></Field>
              <Field label="Nachname" required><Input value={form.nachname} onChange={(e) => upd('nachname', e.target.value)} placeholder="z. B. Huber" /></Field>
            </div>
            <div className="grid-2">
              <Field label="Geburtsdatum"><Input type="date" value={form.geburtsdatum} onChange={(e) => upd('geburtsdatum', e.target.value)} /></Field>
              <Field label="Geschlecht">
                <div className="segmented" style={{ width: '100%' }}>
                  {(['m', 'w', 'd'] as const).map((v) => (
                    <button key={v} type="button" onClick={() => upd('geschlecht', v)} className={form.geschlecht === v ? 'active' : ''} style={{ flex: 1, justifyContent: 'center' }}>{v}</button>
                  ))}
                </div>
              </Field>
            </div>
          </div>
        )}

        {step === 1 && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            <Field label="Straße & Hausnummer"><Input value={form.strasse} onChange={(e) => upd('strasse', e.target.value)} placeholder="Marktplatz 1" /></Field>
            <div className="grid-2">
              <Field label="PLZ" error={plzErr}><Input value={form.plz} onChange={(e) => upd('plz', e.target.value)} maxLength={4} inputMode="numeric" /></Field>
              <Field label="Ort"><Input value={form.ort} onChange={(e) => upd('ort', e.target.value)} /></Field>
            </div>
            <div className="grid-2">
              <Field label="Telefon" error={phoneErr}><Input className="t-mono" value={form.telefon} onChange={(e) => upd('telefon', e.target.value)} placeholder="+43 …" /></Field>
              <Field label="E-Mail" error={emailErr}><Input type="email" className="t-mono" value={form.email} onChange={(e) => upd('email', e.target.value)} placeholder="name@example.at" /></Field>
            </div>
          </div>
        )}

        {step === 2 && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <Field label="Sektion(en)" hint="Mehrfachauswahl möglich — z. B. Fußball + Schi.">
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                {sections.map((s) => (
                  <Chip key={s.id} active={secs.has(s.id)} onClick={() => toggleSec(s.id)}>
                    <span className="sec-dot" style={{ background: s.color }} />
                    {s.name}
                  </Chip>
                ))}
              </div>
            </Field>
            {secs.size > 0 && (
              <Field label="Funktion je Sektion" hint={'Standard ist „Mitglied" — bei Bedarf anpassen.'}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {[...secs.keys()].map((id) => {
                    const sec = sections.find((s) => s.id === id);
                    if (!sec) return null;
                    return (
                      <div key={id} style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                        <span className="sec-dot" style={{ background: sec.color, flexShrink: 0 }} />
                        <span style={{ flex: 1, fontSize: 14 }}>{sec.name}</span>
                        <select
                          className="input"
                          style={{ width: 190, flexShrink: 0 }}
                          value={secs.get(id) ?? 'Mitglied'}
                          onChange={(e) => setSecFn(id, e.target.value)}
                        >
                          {FUNKTIONEN.map((f) => <option key={f} value={f}>{f}</option>)}
                        </select>
                      </div>
                    );
                  })}
                </div>
              </Field>
            )}
            <Field label="Mitgliedsart" hint={forceJugend ? 'Automatisch Jugendmitglied (unter 18 Jahre)' : undefined}>
              <select
                className="input"
                value={effectiveMitgliedsart}
                onChange={(e) => upd('mitgliedsart', e.target.value as Form['mitgliedsart'])}
                disabled={forceJugend}
              >
                <option value="aktiv">Aktives Mitglied</option>
                <option value="unterstuetzend">Unterstützendes Mitglied</option>
                <option value="jugend">Jugendmitglied</option>
                <option value="ehrenmitglied">Ehrenmitglied</option>
              </select>
            </Field>
            <div className="muted-card" style={{ display: 'flex', gap: 10, alignItems: 'flex-start', fontSize: 13 }}>
              <Info size={14} className="text-accent" style={{ flexShrink: 0, marginTop: 2 }} />
              <span>{MITGLIEDSART_INFO[effectiveMitgliedsart]}</span>
            </div>
            <Field label="Eintrittsdatum" hint="Optional — nur ausfüllen, wenn bekannt.">
              <Input type="date" value={form.eingetreten_am} onChange={(e) => upd('eingetreten_am', e.target.value)} />
            </Field>
          </div>
        )}

        {step === 3 && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <Avatar name={initials(form.vorname || 'N', form.nachname || 'N')} size="lg" />
              <div>
                <div className="t-title-3">{form.vorname || 'Vorname'} {form.nachname || 'Nachname'}</div>
                <div className="text-tertiary" style={{ fontSize: 13 }}>{MITGLIEDSART_LABEL[effectiveMitgliedsart]}</div>
              </div>
            </div>
            <div className="kv-list" style={{ background: 'var(--bg-muted)', borderRadius: 10, padding: '4px 14px' }}>
              <KV k="Sektionen">
                {secs.size === 0
                  ? '–'
                  : [...secs.entries()]
                      .map(([id, fn]) => {
                        const nm = sections.find((s) => s.id === id)?.name ?? id;
                        return fn && fn !== 'Mitglied' ? `${nm} (${fn})` : nm;
                      })
                      .join(', ')}
              </KV>
              <KV k="Eintritt">{form.eingetreten_am || 'nicht angegeben'}</KV>
            </div>
            <div className="muted-card" style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
              <ShieldCheck size={16} className="text-accent" style={{ flexShrink: 0, marginTop: 1 }} />
              <span style={{ fontSize: 13 }}>
                Beim Speichern wird ein Audit-Log-Eintrag erstellt und die DSGVO-Einwilligung (v2.4) dokumentiert.
              </span>
            </div>
          </div>
        )}
      </div>
    </Modal>
  );
}
